import { DEAL_LOST_REASONS } from '@veem/schema'
import { z } from 'zod'

const id = z.string().trim().min(1).max(200)
const version = z.number().int().positive()
const nullableText = z.string().trim().max(10000).nullable()
const decimal = z.string().regex(/^\d{1,12}(\.\d{1,3})?$/)
const positive = decimal.refine((value) => /[1-9]/.test(value), 'Введіть число більше нуля')
const timestamp = z.iso.datetime({ offset: true })
const date = z.iso.date()
export const salesLineSchema = z
  .object({
    id: id.optional(),
    productId: id,
    quantity: positive,
    unitPrice: decimal,
  })
  .strict()
export const salesOrderCreateSchema = z
  .object({
    name: z.string().trim().min(1).max(500),
    clientId: id,
    contactIds: z.array(id).max(100),
    sellerEntityId: id,
    currencyCode: z.string().regex(/^[A-Z]{3}$/),
    lines: z
      .array(salesLineSchema.omit({ id: true }))
      .min(1)
      .max(200),
    expectedCloseAt: timestamp.nullable(),
    internalComment: z.string().trim().max(10000),
  })
  .strict()
export const salesOrderVersionSchema = z.object({ id, version }).strict()
export const salesDealUpdateSchema = salesOrderVersionSchema.extend({
  patch: z
    .object({
      name: z.string().trim().min(1).max(500).optional(),
      managerId: id.optional(),
      sellerEntityId: id.nullable().optional(),
      expectedCloseAt: timestamp.nullable().optional(),
      internalComment: nullableText.optional(),
    })
    .strict()
    .refine((value) => Object.keys(value).length > 0),
})
export const salesStageSchema = salesOrderVersionSchema.extend({ stageKey: id })
export const salesLoseSchema = salesOrderVersionSchema.extend({
  stageKey: id.optional(),
  reason: z.enum(DEAL_LOST_REASONS),
  note: nullableText,
})
export const salesContactToggleSchema = salesOrderVersionSchema.extend({ personId: id })
export const salesLinesSchema = salesOrderVersionSchema.extend({
  lines: z.array(salesLineSchema).min(1).max(200),
})
export const salesProposalStatusSchema = salesOrderVersionSchema.extend({
  proposalId: id,
  status: z.enum(['draft', 'review', 'sent', 'accepted', 'rejected', 'overdue', 'superseded']),
})
export const salesEngineeringSchema = salesOrderVersionSchema.extend({
  engineerId: id.nullable(),
  helperIds: z.array(id).max(20).optional(),
  note: z.string().trim().max(10000),
  dueAt: timestamp,
  priority: z.enum(['low', 'medium', 'high']).default('medium'),
  source: z
    .discriminatedUnion('kind', [
      z.object({ kind: z.literal('order') }).strict(),
      z.object({ kind: z.literal('manual') }).strict(),
      z
        .object({ kind: z.literal('monthly'), rowId: id, month: z.string(), label: z.string() })
        .strict(),
    ])
    .default({ kind: 'order' }),
})
export const salesHandoffSchema = salesOrderVersionSchema.extend({
  /** Optional: also give this engineer a task for the new product. Never required. */
  engineerId: id.nullable().default(null),
  /** Ask for an engineer without naming one; the chief engineer assigns it. */
  needsEngineer: z.boolean().default(false),
  stageKey: id.optional(),
  /** Route for products without an approved техкарта, picked by the manager. */
  stageKeys: z.array(id).max(100).optional(),
})
export const salesInternalOrderSchema = z
  .object({
    productName: z.string().trim().min(1).max(500),
    productId: id.nullable(),
    stageKeys: z.array(id).max(100),
    quantity: positive,
    unit: z.string().trim().min(1).max(30),
    requester: z
      .object({
        type: z.enum(['warehouse', 'work_center']),
        name: z.string().trim().min(1).max(200),
      })
      .strict(),
    deadline: date,
    priority: z.enum(['low', 'medium', 'high']),
    note: nullableText,
    /** Who takes the first stage. Any active employee, the administrator included. */
    assigneeId: id.nullable().optional(),
  })
  .strict()
export type SalesOrderCreate = z.infer<typeof salesOrderCreateSchema>
export type SalesOrderVersion = z.infer<typeof salesOrderVersionSchema>
export type SalesDealUpdate = z.infer<typeof salesDealUpdateSchema>
export type SalesStage = z.infer<typeof salesStageSchema>
export type SalesLose = z.infer<typeof salesLoseSchema>
export type SalesContactToggle = z.infer<typeof salesContactToggleSchema>
export type SalesLines = z.infer<typeof salesLinesSchema>
export type SalesProposalStatus = z.infer<typeof salesProposalStatusSchema>
export type SalesEngineering = z.infer<typeof salesEngineeringSchema>
export type SalesHandoff = z.infer<typeof salesHandoffSchema>
export type SalesInternalOrder = z.infer<typeof salesInternalOrderSchema>
